import { KeyboardEvent, useEffect, useState } from "react";
import { Formik } from "formik";
import { DateTime } from "luxon";
import { Box } from "../../../../../components/Box";
import { Flex } from "../../../../../components/Flex";
import { Button } from "../../../../../components/Button";
import { Input } from "../../../../../components/Input";
import Select from "../../../../../components/Select";
import { DatePicker } from "../../../../../components/DatePicker";
import { Paragraph } from "../../../../../components/Paragraph";
import { entityEditValidation } from "../../../../../utils/validationSchema";
import { useAuth } from "../../../../../context/AuthContext";

export type EntityEditValues = {
  name: string;
  country: number | null;
  entityType: number | null;
  status: number | null;
  registrationNumber: string;
  dateOfRegistration: string;
};

export const EditEntityDetailsForm = ({
  initialValues,
  entityTypeOptions,
  countryOptions,
  statusOptions,
  onCancel,
  onSubmit,
}: {
  initialValues: EntityEditValues;
  entityTypeOptions: { label: string; value: number }[];
  countryOptions: { label: string; value: number }[];
  statusOptions: { label: string; value: number }[];
  onCancel: () => void;
  onSubmit: (values: EntityEditValues) => Promise<void> | void;
}) => {
  const { hasPermission } = useAuth();

  const getDatePickerValue = (value: string): Date | string | null => {
    if (!value) return null;

    const isoDate = DateTime.fromISO(value);
    if (isoDate.isValid) {
      return isoDate.toJSDate();
    }

    return value;
  };

  const normalizeDateValue = (value: Date | string | null): string => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return DateTime.fromJSDate(value).toISODate() ?? "";
    }

    if (typeof value === "string") {
      const isoDate = DateTime.fromISO(value);
      if (isoDate.isValid) {
        return isoDate.toISODate() ?? "";
      }

      const formattedDate = DateTime.fromFormat(value, "dd/MM/yyyy");
      if (formattedDate.isValid) {
        return formattedDate.toISODate() ?? "";
      }

      return value;
    }

    return "";
  };

  const [registrationDateValue, setRegistrationDateValue] = useState<
    Date | string | null
  >(() => getDatePickerValue(initialValues.dateOfRegistration));
  const [resetDateOnNextKeypress, setResetDateOnNextKeypress] = useState(
    Boolean(initialValues.dateOfRegistration),
  );

  useEffect(() => {
    setRegistrationDateValue(getDatePickerValue(initialValues.dateOfRegistration));
    setResetDateOnNextKeypress(Boolean(initialValues.dateOfRegistration));
  }, [initialValues.dateOfRegistration]);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={entityEditValidation}
      onSubmit={async (values, helpers) => {
        try {
          await onSubmit({
            ...values,
            dateOfRegistration: normalizeDateValue(values.dateOfRegistration),
          });
        } finally {
          helpers.setSubmitting(false);
        }
      }}
    >
      {(formik) => {
        const isSaveDisabled =
          !formik.dirty ||
          !formik.isValid ||
          formik.isSubmitting ||
          !hasPermission("profile.edit");

        const handleDateKeyDownCapture = (
          event: KeyboardEvent<HTMLDivElement>,
        ) => {
          if (!resetDateOnNextKeypress) return;
          if (!/^\d$/.test(event.key)) return;

          setRegistrationDateValue(null);
          formik.setFieldValue("dateOfRegistration", "");
          setResetDateOnNextKeypress(false);
        };

        return (
          <form onSubmit={formik.handleSubmit}>
            <Box mb={3}>
              <Select
                label="Entity Type"
                name="entityType"
                value={entityTypeOptions.find(
                  (o) => o.value === formik.values.entityType,
                )}
                options={entityTypeOptions}
                onChange={(opt: any) =>
                  formik.setFieldValue("entityType", opt?.value ?? null)
                }
                isRequired
                hasError={Boolean(
                  formik.touched.entityType && formik.errors.entityType,
                )}
                errorMessage={
                  (formik.touched.entityType &&
                    (formik.errors.entityType as string)) ||
                  ""
                }
              />
            </Box>

            <Box mb={3}>
              <Input
                label="Entity name"
                name="name"
                isRequired
                value={formik.values.name}
                onChange={formik.handleChange}
                hasError={Boolean(formik.touched.name && formik.errors.name)}
                errorMessage={
                  (formik.touched.name && (formik.errors.name as string)) || ""
                }
              />
            </Box>

            <Flex gap="16" mb={3}>
              <Box flex={1}>
                <Select
                  label="Country of registration/establishment"
                  name="country"
                  value={countryOptions.find(
                    (o) => o.value === formik.values.country,
                  )}
                  options={countryOptions}
                  onChange={(opt: any) =>
                    formik.setFieldValue("country", opt?.value ?? null)
                  }
                  hasError={Boolean(
                    formik.touched.country && formik.errors.country,
                  )}
                  errorMessage={
                    (formik.touched.country &&
                      (formik.errors.country as string)) ||
                    ""
                  }
                />
              </Box>

              <Box flex={1}>
                <Select
                  label="Current status"
                  name="status"
                  value={statusOptions.find(
                    (o) => o.value === formik.values.status,
                  )}
                  options={statusOptions}
                  onChange={(opt: any) =>
                    formik.setFieldValue("status", opt?.value ?? null)
                  }
                />
              </Box>
            </Flex>

            <Flex gap="16" mb={3}>
              <Box flex={1}>
                <Input
                  label="Registration number"
                  name="registrationNumber"
                  value={formik.values.registrationNumber}
                  onChange={formik.handleChange}
                  hasError={Boolean(
                    formik.touched.registrationNumber &&
                      formik.errors.registrationNumber,
                  )}
                  errorMessage={
                    (formik.touched.registrationNumber &&
                      (formik.errors.registrationNumber as string)) ||
                    ""
                  }
                />
              </Box>

              <Box flex={1}>
                <div onKeyDownCapture={handleDateKeyDownCapture}>
                  <DatePicker
                    label="Date of registration/establishment"
                    value={registrationDateValue}
                    format="dd/MM/yyyy"
                    maxDate={DateTime.now().toJSDate()}
                    onChange={(d: Date | string | null) => {
                      setRegistrationDateValue(d);
                      formik.setFieldValue(
                        "dateOfRegistration",
                        normalizeDateValue(d),
                      );
                      formik.setFieldTouched("dateOfRegistration", true);
                      setResetDateOnNextKeypress(false);
                    }}
                  />
                </div>
                {formik.touched.dateOfRegistration &&
                  formik.errors.dateOfRegistration && (
                    <Paragraph mt={1} color="red.60" fontSize={1}>
                      {formik.errors.dateOfRegistration as string}
                    </Paragraph>
                  )}
              </Box>
            </Flex>

            <Flex justifyContent="flex-end" gap="12" mt={4}>
              <Button type="button" variant="text" onClick={onCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                isDisabled={isSaveDisabled}
                title={
                  hasPermission("profile.edit")
                    ? undefined
                    : "You don't have permission to edit this profile"
                }
              >
                {formik.isSubmitting ? "Saving..." : "Save"}
              </Button>
            </Flex>
          </form>
        );
      }}
    </Formik>
  );
};
